/**
 * Browser Authentication Flow for CLI
 *
 * Implements the CLI token-based browser authentication flow:
 * 1. Generate unique CLI token
 * 2. Call POST /api/auth/cli/init to register pending auth
 * 3. Open browser with auth URL
 * 4. Poll GET /api/auth/cli/status until completion/timeout/error
 * 5. Return credentials on success
 */

import { hostname, platform } from 'os';

// =============================================================================
// Types
// =============================================================================

export interface BrowserAuthOptions {
  /** API endpoint (e.g., https://api.mutagent.io) */
  endpoint: string;
  /** Timeout in milliseconds (default: 5 minutes) */
  timeout?: number;
  /** Polling interval in milliseconds (default: 2 seconds) */
  pollInterval?: number;
  /** Skip opening browser (for test mode) */
  skipBrowserOpen?: boolean;
}

export interface BrowserAuthResult {
  /** The workspace-scoped API key */
  apiKey: string;
  /** Workspace ID for the scoped key */
  workspaceId: string;
  /** Workspace name for display */
  workspaceName: string;
  /** Organization ID */
  organizationId: string;
  /** Organization name for display */
  organizationName: string;
  /** When the key expires (if applicable) */
  expiresAt?: string;
}

export interface BrowserAuthInitResponse {
  /** URL to open in browser */
  authUrl: string;
  /** Expiration time in seconds */
  expiresIn: number;
}

export type BrowserAuthStatus =
  | 'pending'
  | 'completed'
  | 'expired'
  | 'denied'
  | 'not_found';

export interface BrowserAuthStatusResponse {
  status: BrowserAuthStatus;
  apiKey?: string;
  workspaceId?: string;
  workspaceName?: string;
  organizationId?: string;
  organizationName?: string;
  expiresAt?: string;
  error?: string;
}

// =============================================================================
// Browser Auth Implementation
// =============================================================================

/**
 * Generate a unique CLI token for authentication
 * Uses crypto.randomUUID for cryptographically secure random tokens
 */
export function generateCliToken(): string {
  return crypto.randomUUID();
}

/**
 * Initialize browser authentication flow with the server
 */
export async function initBrowserAuth(
  endpoint: string,
  cliToken: string
): Promise<BrowserAuthInitResponse> {
  const response = await fetch(`${endpoint}/api/auth/cli/init`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      cliToken,
      hostname: hostname(),
      platform: platform(),
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new BrowserAuthError(
      'INIT_FAILED',
      'Failed to initialize browser auth: ' + String(response.status) + ' ' + errorText
    );
  }

  const data = (await response.json()) as BrowserAuthInitResponse;
  return data;
}

/**
 * Poll the server for authentication status
 */
export async function pollAuthStatus(
  endpoint: string,
  cliToken: string
): Promise<BrowserAuthStatusResponse> {
  const response = await fetch(
    `${endpoint}/api/auth/cli/status?token=${encodeURIComponent(cliToken)}`
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new BrowserAuthError(
      'POLL_FAILED',
      'Failed to poll auth status: ' + String(response.status) + ' ' + errorText
    );
  }

  const data = (await response.json()) as BrowserAuthStatusResponse;
  return data;
}

/**
 * Open a URL in the system's default browser
 * In test mode (MUTAGENT_TEST_MODE=true), outputs URL to stdout instead
 * In non-interactive mode, prints URL to stdout for AI agents to display
 */
export async function openBrowser(url: string): Promise<void> {
  // Test mode: output URL to stdout instead of opening browser
  if (process.env.MUTAGENT_TEST_MODE === 'true') {
    console.log(`AUTH_URL:${url}`);
    return;
  }

  // Dynamically import 'open' package
  try {
    const { default: open } = await import('open');
    await open(url);
  } catch {
    // If 'open' package is not available, provide manual instructions
    // In non-interactive mode, this is expected -- just print the URL
    throw new BrowserAuthError(
      'BROWSER_OPEN_FAILED',
      `Could not open browser automatically. Please visit: ${url}`
    );
  }
}

/**
 * Sleep utility for polling
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Perform the complete browser authentication flow
 *
 * @param options - Browser auth options
 * @param onStatusUpdate - Optional callback for status updates (for spinners, etc.)
 * @returns Browser auth result with credentials
 */
export async function performBrowserAuth(
  options: BrowserAuthOptions,
  onStatusUpdate?: (message: string) => void
): Promise<BrowserAuthResult> {
  const {
    endpoint,
    timeout = 300000, // 5 minutes default
    pollInterval = 2000, // 2 seconds default
    skipBrowserOpen = false,
  } = options;

  // 1. Generate unique CLI token
  const cliToken = generateCliToken();

  // 2. Register pending auth with server
  onStatusUpdate?.('Initializing browser authentication...');
  const initResponse = await initBrowserAuth(endpoint, cliToken);
  const { authUrl } = initResponse;

  // 3. Open browser (or output URL in test mode / non-interactive mode)
  if (!skipBrowserOpen) {
    onStatusUpdate?.('Opening browser for authentication...');
    try {
      await openBrowser(authUrl);
    } catch (error) {
      // If browser fails to open, continue but inform user
      // This is expected in non-interactive environments (CI, AI agents)
      if (error instanceof BrowserAuthError && error.code === 'BROWSER_OPEN_FAILED') {
        onStatusUpdate?.(error.message);
      } else {
        throw error;
      }
    }
  }

  // Always print the URL clearly for non-interactive environments (AI agents, CI)
  // This ensures the URL is visible even if the browser failed to open
  console.log('');
  console.log('  Open this URL to authenticate:');
  console.log('');
  console.log('  ' + authUrl);
  console.log('');

  // 4. Poll for completion
  onStatusUpdate?.('Waiting for browser authentication...');
  const startTime = Date.now();

  while (Date.now() - startTime < timeout) {
    await sleep(pollInterval);

    const status = await pollAuthStatus(endpoint, cliToken);

    switch (status.status) {
      case 'pending':
        // Continue polling
        continue;

      case 'completed':
        // Success! Return credentials
        if (
          !status.apiKey ||
          !status.workspaceId ||
          !status.workspaceName ||
          !status.organizationId ||
          !status.organizationName
        ) {
          throw new BrowserAuthError(
            'INCOMPLETE_RESPONSE',
            'Server returned incomplete auth response'
          );
        }
        return {
          apiKey: status.apiKey,
          workspaceId: status.workspaceId,
          workspaceName: status.workspaceName,
          organizationId: status.organizationId,
          organizationName: status.organizationName,
          expiresAt: status.expiresAt,
        };

      case 'expired':
        throw new BrowserAuthError(
          'AUTH_EXPIRED',
          'Browser authentication expired. Please try again.'
        );

      case 'denied':
        throw new BrowserAuthError(
          'AUTH_DENIED',
          status.error ?? 'Browser authentication was denied.'
        );

      case 'not_found':
        throw new BrowserAuthError(
          'TOKEN_NOT_FOUND',
          'Authentication token not found. Please try again.'
        );

      default:
        throw new BrowserAuthError(
          'UNKNOWN_STATUS',
          'Unknown auth status: ' + String(status.status)
        );
    }
  }

  // Timeout
  throw new BrowserAuthError(
    'AUTH_TIMEOUT',
    'Browser authentication timed out after 5 minutes. Please try again.'
  );
}

// =============================================================================
// Error Classes
// =============================================================================

export type BrowserAuthErrorCode =
  | 'INIT_FAILED'
  | 'POLL_FAILED'
  | 'BROWSER_OPEN_FAILED'
  | 'AUTH_EXPIRED'
  | 'AUTH_DENIED'
  | 'AUTH_TIMEOUT'
  | 'TOKEN_NOT_FOUND'
  | 'INCOMPLETE_RESPONSE'
  | 'UNKNOWN_STATUS';

export class BrowserAuthError extends Error {
  code: BrowserAuthErrorCode;

  constructor(code: BrowserAuthErrorCode, message: string) {
    super(message);
    this.name = 'BrowserAuthError';
    this.code = code;
  }

  /**
   * Get user-friendly suggestion based on error code
   */
  getSuggestion(): string {
    switch (this.code) {
      case 'INIT_FAILED':
        return 'Check your endpoint configuration and network connection.';
      case 'POLL_FAILED':
        return 'Check your network connection and try again.';
      case 'BROWSER_OPEN_FAILED':
        return 'Copy the URL above and open it manually in your browser.';
      case 'AUTH_EXPIRED':
      case 'AUTH_TIMEOUT':
        return 'Run "mutagent auth login" again to restart authentication.';
      case 'AUTH_DENIED':
        return 'Ensure you have access to the workspace and try again.';
      case 'TOKEN_NOT_FOUND':
      case 'INCOMPLETE_RESPONSE':
      case 'UNKNOWN_STATUS':
        return 'Please try again. If the issue persists, contact support.';
      default:
        return 'Please try again.';
    }
  }
}
